import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { getSecurityHealth } from '../utils/api';
import {
  Shield, ShieldCheck, ShieldAlert, ShieldX, RefreshCw,
  CheckCircle, AlertTriangle, XCircle, Clock, Download,
} from 'lucide-react';

const STATUS_CONFIG = {
  pass:  { icon: ShieldCheck, color: 'var(--green)', bg: 'transparent',              label: 'Passed'  },
  warn:  { icon: ShieldAlert, color: 'var(--amber)', bg: 'rgba(245,158,11,0.04)',    label: 'Warning' },
  fail:  { icon: ShieldX,     color: 'var(--red)',   bg: 'rgba(239,68,68,0.04)',      label: 'Failed'  },
  error: { icon: XCircle,     color: 'var(--red)',   bg: 'rgba(239,68,68,0.04)',      label: 'Error'   },
};

function SummaryCard({ label, value, color, icon: Icon }) {
  return (
    <div style={{
      padding: '16px 18px', background: 'var(--bg-card)', borderRadius: 10,
      border: '1px solid var(--border)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
        <Icon size={14} style={{ color }} />
      </div>
      <div style={{ fontSize: '1.6rem', fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

export default function SecurityPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getSecurityHealth();
      setData(d);
    } catch (err) { console.error('Failed to load security health:', err); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleExport = () => {
    if (!data?.checks) return;
    const lines = [
      'ZeusCMS Security & Encryption Health Report',
      `Generated: ${new Date().toLocaleString()}`,
      `Next Quarterly Review: ${data.next_review || 'Not scheduled'}`,
      '',
      'Check,Status,Detail,Standard,Provider',
      ...data.checks.map(c =>
        `"${c.label}","${c.status}","${(c.detail || '').replace(/"/g, '""')}","${c.standard || ''}","${c.provider || ''}"`
      ),
      '',
      `Summary: ${data.summary.pass} passed / ${data.summary.warn} warnings / ${data.summary.fail} failed`,
    ];
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `security-health-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const summary = data?.summary || { total: 0, pass: 0, warn: 0, fail: 0 };
  const overallStatus = summary.fail > 0 ? 'fail' : summary.warn > 0 ? 'warn' : 'pass';
  const overallLabel = overallStatus === 'fail' ? 'Action Required' : overallStatus === 'warn' ? 'Needs Attention' : 'All Systems Secure';
  const overallColor = overallStatus === 'fail' ? 'var(--red)' : overallStatus === 'warn' ? 'var(--amber)' : 'var(--green)';

  return (
    <Layout>
      <div className="page-header">
        <div>
          <h1 className="page-title">Security & Encryption Health</h1>
          <p className="page-subtitle">Live verification of platform security controls, encryption, and compliance posture</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-sm" onClick={handleExport} disabled={!data?.checks?.length}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem' }}>
            <Download size={13} /> Export CSV
          </button>
          <button className="btn btn-sm" onClick={load} disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem' }}>
            <RefreshCw size={13} className={loading ? 'spin' : ''} /> {loading ? 'Running...' : 'Run Checks'}
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
          <div className="spinner" style={{ margin: '0 auto 12px' }} />
          Running security health checks...
        </div>
      ) : data ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* KPI Row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            <SummaryCard label="Overall Status" value={overallLabel} color={overallColor} icon={Shield} />
            <SummaryCard label="Checks Passed" value={summary.pass} color="var(--green)" icon={CheckCircle} />
            <SummaryCard label="Warnings" value={summary.warn} color="var(--amber)" icon={AlertTriangle} />
            <SummaryCard label="Failed" value={summary.fail} color="var(--red)" icon={XCircle} />
          </div>

          {/* Checks Table */}
          <div className="card" style={{ overflow: 'hidden' }}>
            <div className="card-header">
              <span className="card-title">Security Controls ({data.checks?.length || 0} checks)</span>
              {data.verified_at && (
                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  Verified {new Date(data.verified_at).toLocaleString()}
                </span>
              )}
            </div>
            <div>
              {(data.checks || []).map(c => {
                const cfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.error;
                const Icon = cfg.icon;
                return (
                  <div key={c.id} style={{
                    display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px',
                    borderBottom: '1px solid var(--border)', background: cfg.bg,
                  }}>
                    <div style={{ flexShrink: 0 }}>
                      <Icon size={18} style={{ color: cfg.color }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>{c.label}</span>
                        <span style={{
                          fontSize: '0.6rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                          background: `${cfg.color}18`, color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.05em',
                        }}>{cfg.label}</span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.4 }}>{c.detail}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                      {c.standard && (
                        <span style={{ fontSize: '0.65rem', fontFamily: 'var(--font-mono)', fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: 'rgba(59,130,246,0.1)', color: 'var(--blue)', whiteSpace: 'nowrap' }}>
                          {c.standard}
                        </span>
                      )}
                      {c.provider && (
                        <span style={{ fontSize: '0.62rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          {c.provider}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Review Schedule */}
          <div className="card" style={{ padding: '16px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Clock size={14} style={{ color: 'var(--gold)' }} />
              <span style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-primary)' }}>Quarterly Review Schedule</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div style={{ background: 'var(--bg-base)', borderRadius: 8, padding: '12px 14px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Last Verification</div>
                <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {data.verified_at ? new Date(data.verified_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Never'}
                </div>
              </div>
              <div style={{ background: 'var(--bg-base)', borderRadius: 8, padding: '12px 14px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Next Review Due</div>
                <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {data.next_review || 'Not scheduled'}
                </div>
              </div>
              <div style={{ background: 'var(--bg-base)', borderRadius: 8, padding: '12px 14px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Review Cadence</div>
                <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>Every 90 days</div>
              </div>
            </div>
          </div>

          {/* Documentation Links */}
          <div className="card" style={{ padding: '16px 18px' }}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-primary)', marginBottom: 10 }}>Security Documentation</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                { title: 'Security Configuration Record', desc: 'Encryption types, key management, infrastructure security, and quarterly verification checklist' },
                { title: 'Secret Rotation Guide', desc: 'Rotation procedures for all 8 secret types with blast radius and generation commands' },
              ].map(doc => (
                <div key={doc.title} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  background: 'var(--bg-base)', borderRadius: 8, border: '1px solid var(--border)',
                }}>
                  <Shield size={14} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)' }}>{doc.title}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 1 }}>{doc.desc}</div>
                  </div>
                  <span style={{ marginLeft: 'auto', fontSize: '0.62rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', flexShrink: 0 }}>server/docs/</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          Failed to load security health data. Click "Run Checks" to try again.
        </div>
      )}
    </Layout>
  );
}
